import React from 'react';
import { useSelector } from 'react-redux';
import { Package, User, MapPin, Truck, Hash, Mail, Phone, CreditCard, ExternalLink } from 'lucide-react';
import { getShippingMethodLabel } from '../../../../../utils/catalogs';
import {
  getFullAddress,
  getOrderProvider,
} from '../../../../../features/orderDetails/orderUtils';
import { SHIPPABLE_ORDER_TYPES } from '../../../../../features/orderDetails/orderConstants';
import { formatTaxId } from '../../../../../features/deliveries/orderUploadUtils';
import { DOCUMENT_LABEL_BY_COUNTRY } from '../../../../../features/deliveries/orderUploadConstants';
import { COUNTRY_ISO2 } from '../../../../../features/geo/geoConstants';
import { sumItemWeights } from '../../../../../features/deliveryItems/deliveryItemsConstants';
import { PickupPointDestination } from '../../../../../components/deliveries/PickupPointDestination';

const Datum = ({ icon: Icon, label, children }) => (
  <div className="space-y-1">
    <span className="text-xs font-semibold text-text-muted uppercase tracking-wider flex items-center gap-1.5">
      {Icon && <Icon size={12} />} {label}
    </span>
    <div className="font-medium text-ninja-dark dark:text-white">{children}</div>
  </div>
);

/**
 * Read-only summary of the order. Every edit lives in the actions menu of the
 * header.
 *
 * The origin warehouse is always shown: it is the seller's own. The driver is
 * not, because assigning one belongs to the admin's checkout plan.
 */
const OrderGeneralTab = ({ order }) => {
  const { data: deliveryItems } = useSelector((state) => state.deliveryItems);

  // The bultos are the reference the backend works from, so their sum is the
  // real weight. `delivery.weight` is derived and goes stale; it only stands in
  // while no bulto has been weighed yet.
  const itemsWeight = sumItemWeights(deliveryItems);
  const weight = itemsWeight > 0
    ? String(itemsWeight)
    : String(order.delivery?.weight || '').trim() || '1000';

  const isShippable = SHIPPABLE_ORDER_TYPES.includes(order.type);
  const isCollect = order.type === 'click_collect';

  const countryIso2 = order.seller?.company?.country_fk?.iso2 || COUNTRY_ISO2.CL;
  const documentLabel = DOCUMENT_LABEL_BY_COUNTRY[countryIso2] || DOCUMENT_LABEL_BY_COUNTRY.CL;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 animate-in slide-in-from-bottom-2 duration-300">

      {/* Información de Entrega */}
      <div className="space-y-6">
        <div
          data-testid="order-general-delivery"
          className="bg-white dark:bg-[#1a1a1a] rounded-2xl p-6 shadow-sm border border-border h-full"
        >
          <h3 className="text-lg font-bold text-ninja-dark dark:text-white mb-6 flex items-center gap-2">
            <Package className="text-ninja-green" size={20} /> Información de Entrega
          </h3>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            <Datum icon={Hash} label="Vendedor">
              {order.seller?.company?.social_reason || 'N/A'}
            </Datum>
            <Datum label="Peso (gramos)">{weight}</Datum>

            {isShippable && (
              <>
                <Datum label="Tipo de Envío">
                  {order.delivery?.shipping_method
                    ? getShippingMethodLabel(order.delivery.shipping_method)
                    : 'N/A'}
                </Datum>
                <Datum icon={Truck} label="Courier">
                  {getOrderProvider(order.delivery)}
                </Datum>
              </>
            )}

            <Datum icon={Package} label="Bodega Origen">
              {order.warehouse?.name || 'N/A'}
            </Datum>

            {isCollect && (
              <Datum icon={Package} label="Punto de Retiro">
                {order.warehouse?.name || 'N/A'}
              </Datum>
            )}

            {isShippable && (
              <Datum label="Tracking">
                {/* The number doubles as the link to the courier's own tracking
                    page, so both live in one row instead of two. */}
                {order.delivery?.master_tracking_number ? (
                  order.delivery?.url_tracking ? (
                    <a
                      href={order.delivery.url_tracking}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-ninja-green hover:underline flex items-center gap-1"
                    >
                      {order.delivery.master_tracking_number} <ExternalLink size={12} />
                    </a>
                  ) : (
                    order.delivery.master_tracking_number
                  )
                ) : 'N/A'}
              </Datum>
            )}

            <Datum label="Guía de Despacho">
              {order.delivery?.dispatch_guide_number || 'N/A'}
            </Datum>

            {order.delivery?.pickup_point && !isCollect ? (
              <PickupPointDestination delivery={order.delivery} Datum={Datum} />
            ) : (
              <div className="col-span-1 sm:col-span-2">
                <Datum icon={MapPin} label="Dirección de Entrega">
                  {isCollect
                    ? getFullAddress(order.warehouse?.address)
                    : getFullAddress(order.delivery?.shipping_address)}
                </Datum>
              </div>
            )}

            <div className="col-span-1 sm:col-span-2 space-y-1">
              <span className="text-xs font-semibold text-text-muted uppercase tracking-wider">
                Nota de Entrega
              </span>
              <p className="font-medium text-text-secondary bg-ninja-gray dark:bg-[#2d2d2d] p-3 rounded-xl text-sm italic">
                {order.notes || 'Sin nota de entrega'}
              </p>
            </div>
          </div>

        </div>
      </div>

      {/* Destinatario */}
      <div className="space-y-6">
        <div
          data-testid="order-general-recipient"
          className="bg-white dark:bg-[#1a1a1a] rounded-2xl p-6 shadow-sm border border-border h-full"
        >
          <h3 className="text-lg font-bold text-ninja-dark dark:text-white mb-6 flex items-center gap-2">
            <User className="text-ninja-green" size={20} /> Destinatario
          </h3>
          <div className="space-y-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-full bg-ninja-gray dark:bg-[#2d2d2d] text-ninja-dark dark:text-white flex items-center justify-center font-bold text-lg">
                {(order.recipient || 'D').charAt(0).toUpperCase()}
              </div>
              <div>
                <span className="text-xs font-semibold text-text-muted uppercase tracking-wider">Nombre</span>
                <p className="font-medium text-ninja-dark dark:text-white text-lg">{order.recipient || 'N/A'}</p>
              </div>
            </div>

            <div className="grid grid-cols-1 gap-6 pt-4 border-t border-border/50">
              <div className="flex items-center gap-4 group">
                <div className="w-10 h-10 rounded-xl bg-ninja-gray dark:bg-[#2d2d2d] flex items-center justify-center text-text-secondary group-hover:bg-ninja-greenLight group-hover:text-ninja-green transition-colors">
                  <Mail size={18} />
                </div>
                <div>
                  <span className="text-xs font-semibold text-text-muted uppercase tracking-wider">Email</span>
                  <p className="font-medium text-ninja-dark dark:text-white">{order.contact_email || 'N/A'}</p>
                </div>
              </div>

              <div className="flex items-center gap-4 group">
                <div className="w-10 h-10 rounded-xl bg-ninja-gray dark:bg-[#2d2d2d] flex items-center justify-center text-text-secondary group-hover:bg-ninja-greenLight group-hover:text-ninja-green transition-colors">
                  <Phone size={18} />
                </div>
                <div>
                  <span className="text-xs font-semibold text-text-muted uppercase tracking-wider">Teléfono</span>
                  <p className="font-medium text-ninja-dark dark:text-white">{order.contact_phone || 'N/A'}</p>
                </div>
              </div>

              {/* Stored plain, shown masked: the column holds the value and
                  this is its presentation. */}
              <div className="flex items-center gap-4 group">
                <div className="w-10 h-10 rounded-xl bg-ninja-gray dark:bg-[#2d2d2d] flex items-center justify-center text-text-secondary group-hover:bg-ninja-greenLight group-hover:text-ninja-green transition-colors">
                  <CreditCard size={18} />
                </div>
                <div>
                  <span className="text-xs font-semibold text-text-muted uppercase tracking-wider">{documentLabel}</span>
                  <p className="font-medium text-ninja-dark dark:text-white">
                    {formatTaxId(order.contact_rut, countryIso2) || 'N/A'}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OrderGeneralTab;
